import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMount } from 'ahooks'
import { LeftOutline } from 'antd-mobile-icons'
import { Swiper, Toast, Image as AntdImage, ImageViewer } from 'antd-mobile'

import { t } from '@/utils/i18n'
import { useCorpSettingStore, useCorpInfoStore, useCartStore } from '@/stores'
import usePageTitle from '@/hooks/usePageTitle'
import {
  apis,
  utils,
  image as imgUtil,
  productUtil,
  uploadEt,
  common,
  uploadCommonEt,
} from '@/utils'
import type { UnitPriceItem, SaleProduct, CloudProduct } from '@/types/product'

import DetailBar from '@/pages/Album/components/DetailBar'
import ProductNotFound from '@/pages/Album/components/ProductNotFound/ProductNotFound'
import ProductGraphic from '@/pages/Products/components/ProductGraphic/ProductGraphic'
import AttrList from '@/pages/Products/components/CloudAttrs/CloudAttrs'
import DetailSkeleton from '@/pages/Products/components/DetailSkeleton/DetailSkeleton'
import KpVideo from '@/components/Video'
import Icon from '@/components/Icon/Icon'
import styles from './index.module.less'

// 导入图片
import defaultImg from '@/assets/img/default/default.png'
import useRefState from '@/hooks/useRefState'
import downloadImageIcon from '@/assets/img/icon/download-image.png'

const { productApi } = apis
const { to, getUuid } = utils
const { formatImgList, getAbsoluteUrl } = imgUtil

interface CloudAttrItem {
  [key: string]: string
}

enum Source {
  APP = 0,
  LINK = 1,
}

function Detail() {
  const { productId } = useParams()
  const { corpSetting } = useCorpSettingStore()
  const { corpInfo } = useCorpInfoStore()
  const [searchParams] = useSearchParams()
  const { corpId, customerId } = corpInfo
  const source = Number(searchParams.get('source')) || 0

  const {
    currencySymbol,
    qualityPrecision,
    pricePrecision,
    cloudStoreStockType,
  } = corpSetting
  const { cart } = useCartStore()
  const { cartId } = cart
  const navigate = useNavigate()

  const [notFound, setNotFound] = useState(false)
  const [product, setProduct] = useState<CloudProduct>({} as CloudProduct) // 货品实例
  const [chooseUnit, setChooseUnit] = useState<UnitPriceItem>() // 选择的单位
  const [mediaList, setMediaList] = useState<string[]>([]) // 媒体列表
  const [video, setVideo] = useState<string>('') // 视频地址
  const [detailImageList, setDetailImageList] = useState<string[]>([]) // 图文详情图片(包含货品主图)
  const [cloudAttrs, setCloudAttrs] = useState<CloudAttrItem[]>([]) // 需要显示的属性
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIsMultiPrice, isMultiPriceRef] = useRefState(false) // 是否不同规格不同价格
  const [contentLoader, setContentLoader] = useState(true)

  const canGoBack = () => {
    return source !== Source.LINK
  }

  usePageTitle()

  // 轮播图
  const items = mediaList.map((item, index) => (
    <Swiper.Item key={index}>
      <AntdImage
        fit="cover"
        width={'100vw'}
        height={'100vw'}
        src={item}
        lazy={true}
        onClick={() => {
          handlePreviewImg(index, mediaList)
        }}
      />
    </Swiper.Item>
  ))

  if (video) {
    items.unshift(
      <Swiper.Item key="video">{<KpVideo src={video} />}</Swiper.Item>,
    )
  }

  // 渲染图片底部
  const renderFooter = (image: string) => {
    return (
      <div className={styles['download-image-box']}>
        <img
          src={downloadImageIcon}
          alt=""
          className={styles['download-image']}
          onClick={() => onDownloadFile(image)}
        />
      </div>
    )
  }

  // 下载单张图片
  function onDownloadFile(image: string) {
    const files: { url: string; type: 'image' | 'video' }[] = [image].map(
      (url) => ({
        url,
        type: 'image',
        name: getUuid() + '.jpg',
      }),
    )
    common.downloadFiles(files)
  }

  function handleGoBack() {
    navigate(-1)
  }

  function handleGoHome() {
    navigate(`/album/${corpId}`)
  }

  // 预览图片
  function handlePreviewImg(index: number, urls: string[]) {
    if (urls[0] === defaultImg) {
      return
    }

    ImageViewer.Multi.show({
      images: urls,
      defaultIndex: index,
      renderFooter,
    })
  }

  const createdSwiperIndicator = (total: number, current: number) => (
    <div className={styles['custom-indicator']}>{`${
      current + 1
    } / ${total}`}</div>
  )

  // 获取货品数据
  async function getGoodsDetail() {
    const sendData = {
      cartId,
      corpId,
      customerId,
      productId,
    }

    const [err, res] = await to(productApi.prodDetail(sendData))

    if (err || !res.data) {
      setNotFound(true)
      return
    }

    setContentLoader(false)

    const { cloudProduct, saleProduct } = res.data

    // 后端存在数据维护不正确的问题，起订单位的下标会出现大于等于单位数组的长度，需要重置下标为0
    if (
      cloudProduct.unitPriceArray &&
      cloudProduct.productMoqUnitIndex > 0 &&
      cloudProduct.productMoqUnitIndex >= cloudProduct.unitPriceArray?.length
    ) {
      cloudProduct.productMoqUnitIndex = 0
    }

    setProduct(cloudProduct)
    setIsMultiPrice((cloudProduct.productStatus & 128) === 128)

    await initMediaList(cloudProduct)
    initChooseUnit(cloudProduct, saleProduct)
    initCloudAttrsSpecification(cloudProduct, cloudProduct.unitPriceArray)
  }

  // 初始化媒体列表
  async function initMediaList(cloudProduct: CloudProduct) {
    const {
      video: rawVideo,
      imageList,
      detailImageList: detailImgList,
    } = cloudProduct
    const mediaList = imageList ? formatImgList(imageList || [], null, 480) : []

    if (rawVideo) {
      const videoTemp = getAbsoluteUrl(rawVideo)
      setVideo(videoTemp)
    }

    // 货品图文详情
    const detailImageList: string[] = detailImgList?.length
      ? formatImgList(detailImgList, null, 480)
      : []

    // 如果没有图片，并且没有视频，则显示默认图片
    if (mediaList.length === 0 && !rawVideo) {
      mediaList.push(defaultImg)
    }

    setMediaList(mediaList)
    setDetailImageList(detailImageList)
  }

  // 根据默认单位索引取单位对象
  function initChooseUnit(cloudProduct: CloudProduct, saleProd: SaleProduct) {
    const currentChooseUnit = productUtil.setChooseUnit(
      { cloudProduct, saleProd },
      isMultiPriceRef.current,
      cloudStoreStockType,
      qualityPrecision,
      pricePrecision,
    )
    setChooseUnit(currentChooseUnit)
  }

  // 初始化显示属性和装箱规格
  function initCloudAttrsSpecification(
    initProduct: CloudProduct,
    unitPriceArray: UnitPriceItem[],
  ) {
    // 组装装箱规格
    const specification = productUtil.getSpecification(unitPriceArray)
    const currentProduct = initProduct || product
    // 获取需要显示的自定义属性
    const keyValues: CloudAttrItem[] = []
    const list = corpSetting.cloudAttrs.filter((item: any) => {
      return item.attrStatus === 1 || item.cloudShowStatus === 1
    })

    list.unshift({
      attrKey: 'packaging',
      attrValue: specification,
    })

    list.forEach((item: any) => {
      if (item.attrKey === 'packaging') {
        keyValues.push({
          key: 'packaging',
          label: t('装箱规格'),
          value: specification,
        })
      } else {
        keyValues.push({
          key: item.attrKey,
          label: item.attrKeyValue,
          value: currentProduct ? currentProduct[item.attrKey] : '',
        })
      }
    })

    const sellingPointIndex = keyValues.findIndex((item: any) => {
      return item.key === 'sellingPoint'
    })

    if (currentProduct.productWeight) {
      const productWeight = {
        key: 'productWeight',
        label: t('重量'),
        value: String(currentProduct.productWeight) + 'g',
      }

      if (sellingPointIndex) {
        keyValues.splice(sellingPointIndex, 0, productWeight)
      } else {
        keyValues.push(productWeight)
      }
    }

    if (
      currentProduct.productLength &&
      currentProduct.productWidth &&
      currentProduct.productHigh
    ) {
      const volume = {
        key: 'volume',
        label: t('体积'),
        value: `${currentProduct.productLength}cm * ${currentProduct.productWidth}cm * ${currentProduct.productHigh}cm`,
      }

      if (sellingPointIndex) {
        keyValues.splice(sellingPointIndex, 0, volume)
      } else {
        keyValues.push(volume)
      }
    }

    setCloudAttrs(keyValues)
  }

  // 数据初始化
  async function init() {
    Toast.show({
      icon: 'loading',
      content: t('加载中…'),
      duration: 0,
    })

    await getGoodsDetail()

    Toast.clear()
  }

  useMount(() => {
    uploadEt({ event_type: 5, products: [{ product_id: productId }] }) // 埋点，货品详情页打开次数
    uploadCommonEt(15) // 7.8.3需求埋点：进入商品详情页并加载完成
    init()
  })

  return (
    <div className={styles['detail-page']}>
      {notFound && <ProductNotFound />}

      {!notFound && !contentLoader && (
        <div className={styles['detail-wrap']}>
          <div className={styles['top-bar']}>
            <div
              className={styles['icon-wrap']}
              onClick={canGoBack() ? handleGoBack : handleGoHome}
            >
              {canGoBack() ? (
                <LeftOutline color="#ffffff" fontSize={18} />
              ) : (
                <Icon name="home" color="#ffffff" size={28} />
              )}
            </div>
          </div>

          <div className={styles['swiper-wrap']}>
            <Swiper
              style={{ '--width': '100vw', '--height': '100vw' }}
              indicator={createdSwiperIndicator}
            >
              {items}
            </Swiper>
          </div>

          <div className={styles['card-wrap'] + ' ' + styles['detail-info']}>
            <div className={styles['price-wrap']}>
              {/* 货品报价和单位 */}
              <p className={styles['price']}>
                {currencySymbol} {chooseUnit?.priceText}
                {chooseUnit?.priceUnitName
                  ? '/' + chooseUnit.priceUnitName
                  : ''}
              </p>

              {/* 原价 */}
              {/* {chooseUnit && chooseUnit.originPriceText.length > 0 && (
                <p className={styles['original-price']}>
                  {currencySymbol}{' '}
                  {corpInfo.accountId ? chooseUnit?.originPriceText : '***'}{' '}
                  <span
                    className={styles['price-tag']}
                    style={{ fontSize: '10px' }}
                  >
                    -{chooseUnit?.discount}
                  </span>
                </p>
              )} */}
            </div>
            {/* 隐藏VAT */}
            {/* <div className={styles['tag-wrap']}>
              <span className={styles["tax-tag"]}>{t("不含税")}</span>
            </div> */}
            {/* 货品标题 */}
            <h1 className={styles['product-title'] + ' ep-2'}>
              {!!product.cloudProduct && (
                <span className={productUtil.getRecommendClass(product)}>
                  {productUtil.getRecommendTag(product)}
                </span>
              )}

              {product.productName}
            </h1>

            {/* 起订量 */}
            {product && Number(product.productMoqQty) > 0 && (
              <p className={styles['text-wrap']}>
                MOQ:
                {product.productMoqQty}
                {product.unitPriceArray &&
                product.unitPriceArray[product.productMoqUnitIndex].unitName
                  ? product.unitPriceArray[product.productMoqUnitIndex].unitName
                  : ''}
              </p>
            )}
          </div>

          {/* 自定义属性列表 */}
          {product && <AttrList attrList={cloudAttrs} />}

          {/* 货品图片详细 */}
          <ProductGraphic
            mediaList={detailImageList}
            visible={Boolean(detailImageList.length)}
            detailImageClick={(index) =>
              handlePreviewImg(index, detailImageList)
            }
          />

          <DetailBar
            productName={product.productName}
            mediaList={mediaList}
            video={video}
          />
        </div>
      )}

      {contentLoader && <DetailSkeleton />}
    </div>
  )
}

export default Detail
